import React from 'react';
import { Link } from 'react-router-dom';
import { X, CheckCircle, Clock, Sparkles, ArrowRight, Shield, Dumbbell, UserCheck, BookOpen } from 'lucide-react';

// Map ConditionDetailModal id → ConditionPage slug for SEO link
const CONDITION_ID_TO_SLUG = {
  1: 'kronicne-bolecine',
  2: 'pooperativna-rehabilitacija',
  3: 'sportne-poskodbe',
  4: 'preventiva-mobilnost',
  5: 'individualna-vadba',
  6: 'drza-svetovanje',
};

// Podatki o področjih obravnave - posamezne spremenljivke namesto gnezdenih objektov
const getConditionData = (id) => {
  switch(id) {
    case 1:
      return {
        title: 'Kronične bolečine',
        description: 'Bolečine v hrbtu, vratu, sklepih in mišicah',
        color: 'from-red-500 to-rose-400',
        intro: 'Kronične bolečine so pogosto posledica dolgotrajnih obremenitev, nepravilne drže ali preteklih poškodb. Naš pristop je celovit in usmerjen v odpravo vzroka, ne le simptomov.',
        step1Title: 'Ocena stanja',
        step1Desc: 'Temeljit pregled gibljivosti, drže in bolečinskih točk za natančno diagnozo.',
        step2Title: 'Manualna terapija',
        step2Desc: 'Tehnike mobilizacije sklepov in mehkih tkiv za zmanjšanje napetosti in izboljšanje gibljivosti.',
        step3Title: 'TECAR/Laser terapija',
        step3Desc: 'Napredne tehnologije za globinsko zdravljenje in pospeševanje celjenja tkiv.',
        step4Title: 'Terapevtska vadba',
        step4Desc: 'Individualno prilagojene vaje za krepitev in stabilizacijo prizadetih področij.',
        duration: '4-8 tednov',
        sessions: '2-3x tedensko',
        result: 'Zmanjšanje bolečin, izboljšana gibljivost'
      };
    case 2:
      return {
        title: 'Pooperativna rehabilitacija',
        description: 'Okrevanje po ortopedskih in drugih operacijah',
        color: 'from-blue-500 to-cyan-400',
        intro: 'Po operativnem posegu je pravilna rehabilitacija ključna za uspešno okrevanje. Naš program je prilagojen vrsti operacije in vašim potrebam.',
        step1Title: 'Začetna faza',
        step1Desc: 'Nežne tehnike za zmanjšanje otekline, obvladovanje bolečin in ohranjanje gibljivosti.',
        step2Title: 'Aktivna rehabilitacija',
        step2Desc: 'Postopno povečevanje obremenitev in vaje za obnovo mišične moči.',
        step3Title: 'Funkcionalna vadba',
        step3Desc: 'Vaje za povrnitev vsakodnevnih aktivnosti in športnih gibanj.',
        step4Title: 'Vrnitev v aktivnost',
        step4Desc: 'Priprava na vrnitev v šport ali zahtevnejše fizične aktivnosti.',
        duration: '6-12 tednov',
        sessions: '2-3x tedensko',
        result: 'Popolno okrevanje in vrnitev k normalnim aktivnostim'
      };
    case 3:
      return {
        title: 'Športne poškodbe',
        description: 'Zvini, nategi, pretrgane vezi, mišične poškodbe',
        color: 'from-amber-500 to-yellow-400',
        intro: 'Športne poškodbe zahtevajo specializiran pristop za hitro in varno vrnitev v aktivnost. Kombiniramo najnovejše tehnike z izkušnjami dela s profesionalnimi športniki.',
        step1Title: 'Akutna obravnava',
        step1Desc: 'Takojšnja pomoč za zmanjšanje bolečin, otekline in preprečevanje nadaljnje škode.',
        step2Title: 'Diagnostika',
        step2Desc: 'Natančna ocena poškodbe in izdelava individualiziranega načrta zdravljenja.',
        step3Title: 'Aktivna rehabilitacija',
        step3Desc: 'Progresivne vaje za obnovo moči, stabilnosti in športno-specifičnih gibanj.',
        step4Title: 'Preventiva ponovitev',
        step4Desc: 'Program za preprečevanje ponovnih poškodb in optimizacijo športne učinkovitosti.',
        duration: '2-8 tednov',
        sessions: '2-4x tedensko',
        result: 'Hitra in varna vrnitev v šport'
      };
    case 4:
      return {
        title: 'Preventiva in mobilnost',
        description: 'Ohranjanje gibljivosti in preprečevanje poškodb',
        color: 'from-emerald-500 to-green-400',
        iconType: 'Shield',
        intro: 'Preventiva je najboljše zdravljenje. Naš program je zasnovan za ohranjanje optimalne gibljivosti in preprečevanje poškodb pri aktivnih posameznikih vseh starosti.',
        step1Title: 'Funkcionalna ocena',
        step1Desc: 'Analiza gibljivosti, ravnotežja in mišičnih neravnovesij za identifikacijo tveganj.',
        step2Title: 'Mobilizacijske tehnike',
        step2Desc: 'Manualne tehnike za ohranjanje gibljivosti sklepov in elastičnosti tkiv.',
        step3Title: 'Individualni program',
        step3Desc: 'Priprava domačih vaj za vzdrževanje rezultatov in samostojno vadbo.',
        step4Title: 'Redno spremljanje',
        step4Desc: 'Periodične kontrole za prilagajanje programa in spremljanje napredka.',
        duration: 'Kontinuirano',
        sessions: '1-2x mesečno',
        result: 'Dolgoročna gibljivost in vitalnost'
      };
    case 5:
      return {
        title: 'Individualna vadba',
        description: 'Prilagojene vaje za vaše specifične potrebe',
        color: 'from-violet-500 to-purple-400',
        iconType: 'Dumbbell',
        intro: 'Vsak posameznik je edinstven. Naša individualna vadba je popolnoma prilagojena vašim ciljem, zmožnostim in morebitnim omejitvam.',
        step1Title: 'Začetni posvet',
        step1Desc: 'Pogovor o vaših ciljih, zdravstvenem stanju in predhodnih izkušnjah.',
        step2Title: 'Testiranje',
        step2Desc: 'Ocena trenutne telesne pripravljenosti, moči, gibljivosti in ravnotežja.',
        step3Title: 'Načrt vadbe',
        step3Desc: 'Izdelava personaliziranega programa vadbe s progresijo.',
        step4Title: 'Vodena vadba',
        step4Desc: 'Individualno vodene vadbene enote s stalnim nadzorom in prilagoditvami.',
        duration: 'Po dogovoru',
        sessions: '1-3x tedensko',
        result: 'Doseganje osebnih ciljev'
      };
    case 6:
      return {
        title: 'Drža in svetovanje',
        description: 'Korekcija drže in ergonomsko svetovanje',
        color: 'from-[#f87171] to-[#fb923c]',
        iconType: 'UserCheck',
        intro: 'Nepravilna drža je pogost vzrok za bolečine in poškodbe. Naš program združuje korekcijo drže z ergonomskim svetovanjem za trajne rezultate.',
        step1Title: 'Analiza drže',
        step1Desc: 'Podrobna ocena drže v stoječem, sedečem položaju in med gibanjem.',
        step2Title: 'Korektivne tehnike',
        step2Desc: 'Manualne tehnike in vaje za izboljšanje drže in odpiranje skrajšanih struktur.',
        step3Title: 'Ergonomsko svetovanje',
        step3Desc: 'Nasveti za pravilno ureditev delovnega mesta in vsakodnevnih aktivnosti.',
        step4Title: 'Domači program',
        step4Desc: 'Vaje in navade za vzdrževanje pravilne drže v domačem okolju.',
        duration: '4-6 tednov',
        sessions: '1-2x tedensko',
        result: 'Izboljšana drža, manj bolečin'
      };
    default:
      return null;
  }
};

const ConditionDetailModal = ({ conditionId, image, onClose, onBook }) => {
  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (conditionId) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [conditionId]);

  if (!conditionId) return null;
  
  const data = getConditionData(conditionId);
  if (!data) return null;

  const renderIcon = () => {
    if (image) {
      return <img loading="lazy" decoding="async" src={image} alt={data.title} className="w-16 h-16 object-contain" />;
    }
    if (data.iconType === 'Shield') return <Shield className="w-10 h-10 text-white" />;
    if (data.iconType === 'Dumbbell') return <Dumbbell className="w-10 h-10 text-white" />;
    if (data.iconType === 'UserCheck') return <UserCheck className="w-10 h-10 text-white" />;
    return null;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={onClose}
      data-testid="condition-modal-overlay"
    >
      <div className="min-h-full flex items-start md:items-center justify-center p-4 py-8">
        <div 
          className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="condition-modal"
        >
        <div className={`relative bg-gradient-to-br ${data.color} p-6 md:p-8 rounded-t-3xl`}>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
            data-testid="close-condition-modal"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
              {renderIcon()}
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">{data.title}</h3>
              <p className="text-white/80 text-sm md:text-base">{data.description}</p>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8">
          <p className="text-slate-600 text-base leading-relaxed mb-8">{data.intro}</p>

          <h4 className="text-lg font-bold text-[#1a365d] mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-[#f87171]/10 rounded-lg flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-[#f87171]" />
            </span>
            Kako poteka obravnava
          </h4>
          
          <div className="space-y-4 mb-8">
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
              <div className={`w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br ${data.color} flex items-center justify-center text-white font-bold text-sm`}>1</div>
              <div>
                <h5 className="font-semibold text-[#1a365d] mb-1">{data.step1Title}</h5>
                <p className="text-slate-600 text-sm leading-relaxed">{data.step1Desc}</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
              <div className={`w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br ${data.color} flex items-center justify-center text-white font-bold text-sm`}>2</div>
              <div>
                <h5 className="font-semibold text-[#1a365d] mb-1">{data.step2Title}</h5>
                <p className="text-slate-600 text-sm leading-relaxed">{data.step2Desc}</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
              <div className={`w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br ${data.color} flex items-center justify-center text-white font-bold text-sm`}>3</div>
              <div>
                <h5 className="font-semibold text-[#1a365d] mb-1">{data.step3Title}</h5>
                <p className="text-slate-600 text-sm leading-relaxed">{data.step3Desc}</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
              <div className={`w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br ${data.color} flex items-center justify-center text-white font-bold text-sm`}>4</div>
              <div>
                <h5 className="font-semibold text-[#1a365d] mb-1">{data.step4Title}</h5>
                <p className="text-slate-600 text-sm leading-relaxed">{data.step4Desc}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <Clock className="w-5 h-5 text-blue-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500 mb-1">Trajanje</p>
              <p className="font-semibold text-[#1a365d] text-sm">{data.duration}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500 mb-1">Pogostost</p>
              <p className="font-semibold text-[#1a365d] text-sm">{data.sessions}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <Sparkles className="w-5 h-5 text-purple-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500 mb-1">Rezultat</p>
              <p className="font-semibold text-[#1a365d] text-xs leading-tight">{data.result}</p>
            </div>
          </div>

          <button
            onClick={() => { onClose(); onBook(); }}
            className={`w-full py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${data.color} hover:opacity-90 transition-all flex items-center justify-center gap-2`}
            data-testid="book-from-condition-modal"
          >
            Rezervirajte termin
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* SEO link: celoten vodnik */}
          {CONDITION_ID_TO_SLUG[conditionId] && (
            <Link
              to={`/${CONDITION_ID_TO_SLUG[conditionId]}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-[#1a365d] text-[#1a365d] rounded-xl py-3 font-semibold text-sm hover:bg-[#1a365d] hover:text-white transition-all"
              data-testid={`condition-read-full-${conditionId}`}
            >
              <BookOpen className="w-4 h-4" />
              Preberi celoten vodnik
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ConditionDetailModal;
